import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import {
  HelpCircle,
  BookOpen,
  FileText,
  Lightbulb,
  ArrowRight,
  Rocket,
  Upload,
  Search,
  CheckSquare
} from 'lucide-react';

const helpSections = [
  {
    title: 'FAQ',
    description: 'Common questions about PageMind features and workflows',
    icon: HelpCircle,
    to: '/help/faq',
    color: 'text-blue-500'
  },
  {
    title: 'User Guides',
    description: 'Step-by-step guides for getting the most out of PageMind',
    icon: BookOpen,
    to: '/help/guides',
    color: 'text-green-500'
  }
];

const quickLinks = [
  {
    title: 'Website Onboarding Guide',
    description: 'Complete walkthrough for onboarding a new website from scratch to AEO-ready content',
    icon: Rocket,
    to: '/help/guides/onboarding'
  },
  {
    title: 'Feed Import Guide',
    description: 'How to upload, configure, and process product feeds',
    icon: Upload,
    to: '/help/guides#feed-import'
  },
  {
    title: 'Category Research Guide',
    description: 'Understanding and running category research for better AEO',
    icon: Search,
    to: '/help/guides#category-research'
  },
  {
    title: 'Content Review Guide',
    description: 'How to review and approve AI-generated content',
    icon: CheckSquare,
    to: '/help/guides#content-review'
  }
];

export default function HelpCenter() {
  return (
    <div className="container mx-auto p-6 max-w-6xl">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Help & Learning</h1>
        <p className="text-muted-foreground">
          Find answers, learn best practices, and get the most out of PageMind
        </p>
      </div>

      {/* Main Sections */}
      <div className="grid gap-6 md:grid-cols-2 mb-8">
        {helpSections.map((section) => (
          <Link key={section.to} to={section.to}>
            <Card className="h-full hover:border-primary/50 transition-colors cursor-pointer">
              <CardHeader>
                <CardTitle className="flex items-center gap-3">
                  <section.icon className={`h-6 w-6 ${section.color}`} />
                  {section.title}
                </CardTitle>
                <CardDescription>{section.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <span className="inline-flex items-center text-sm text-primary font-medium">
                  Browse {section.title.toLowerCase()}
                  <ArrowRight className="h-4 w-4 ml-1" />
                </span>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      {/* Quick Links */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <Lightbulb className="h-5 w-5 text-yellow-500" />
          Quick Start Guides
        </h2>
        <div className="grid gap-4 md:grid-cols-2">
          {quickLinks.map((link) => (
            <Link key={link.to} to={link.to}>
              <Card className="hover:border-primary/50 transition-colors cursor-pointer">
                <CardContent className="pt-4">
                  <div className="flex items-start gap-3">
                    <link.icon className="h-5 w-5 text-muted-foreground mt-0.5" />
                    <div>
                      <h3 className="font-medium">{link.title}</h3>
                      <p className="text-sm text-muted-foreground">{link.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      </div>

      {/* Additional Resources */}
      <Card className="bg-muted/50">
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <FileText className="h-8 w-8 text-muted-foreground" />
            <div>
              <h3 className="font-semibold mb-1">Need More Help?</h3>
              <p className="text-sm text-muted-foreground mb-3">
                If you can't find what you're looking for, our team is here to help.
                Reach out through the feedback system or contact support directly.
              </p>
              <Link
                to="/my-suggestions"
                className="text-sm text-primary hover:underline inline-flex items-center"
              >
                Submit a question or suggestion
                <ArrowRight className="h-4 w-4 ml-1" />
              </Link>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
